import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EllipsisVerticalIcon, EyeIcon, ArrowDownTrayIcon, ArrowPathIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ReportService } from '../../../shared/services/reportService';
import DownloadFormatModal from './DownloadFormatModal';

export default function ReportActionsMenu({ report, onDeleted, onRegenerated, onToast }) {
  const [open, setOpen] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleRegenerate = async () => {
    setOpen(false);
    try {
      await ReportService.regenerate(report.id);
      onToast?.('Report regeneration queued.', 'success');
      onRegenerated?.();
    } catch {
      onToast?.('Failed to regenerate report.', 'error');
    }
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    try {
      await ReportService.delete(report.id);
      onDeleted?.(report.id);
    } catch {
      onToast?.('Failed to delete report.', 'error');
    }
  };

  return (
    <>
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setOpen((v) => !v)}
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
          title="Actions"
        >
          <EllipsisVerticalIcon className="w-4 h-4" />
        </button>

        {open && (
          <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 z-20 py-1 animate-scale-in">
            <button
              onClick={() => { setOpen(false); navigate(`/admin/reports/${report.id}`); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <EyeIcon className="w-4 h-4 text-gray-400" />
              View
            </button>

            {report.status === 'GENERATED' && (
              <button
                onClick={() => { setOpen(false); setShowDownload(true); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <ArrowDownTrayIcon className="w-4 h-4 text-gray-400" />
                Download
              </button>
            )}

            {(report.status === 'GENERATED' || report.status === 'FAILED') && (
              <button
                onClick={handleRegenerate}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <ArrowPathIcon className="w-4 h-4 text-gray-400" />
                Regenerate
              </button>
            )}

            <div className="my-1 border-t border-gray-100 dark:border-gray-700" />

            <button
              onClick={() => { setOpen(false); setConfirmDelete(true); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <TrashIcon className="w-4 h-4" />
              Delete
            </button>
          </div>
        )}
      </div>

      {showDownload && (
        <DownloadFormatModal
          reportId={report.id}
          onClose={() => setShowDownload(false)}
          onError={(msg) => onToast?.(msg, 'error')}
        />
      )}

      {/* Confirm delete dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setConfirmDelete(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 dark:text-white">Delete Report</h3>
              <button onClick={() => setConfirmDelete(false)}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
              Are you sure you want to delete this report? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(false)} className="text-sm px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                Cancel
              </button>
              <button onClick={handleDelete} className="text-sm px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
